import { lazy } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";

import { MainLayout } from "@gyandiksha/shared-ui/layouts";
import { ProtectedRoute } from "@gyandiksha/shared-ui/routes";
import { defaultSidebarMenuConfig } from "@/navigation/sidebarmenu.config";
import { PageLoader, PageNotFound } from "@repo/design-system/components/ui";

// Sidebar menu config
import { getFirstNavigationPath } from '@gyandiksha/shared-ui/utils';
import { getAuthProps } from '@gyandiksha/shared-ui/services';
import { clearCompanyLogin, isCompanyLogin, setCompanyLogin } from '@/utils';
import { MODULE_IDS, useSidebarMenuConfig } from '@gyandiksha/shared-ui/hooks/useSidebarMenuConfig.js';

// Public pages (login / forgot-password / reset share the Authentication shell)
const Authentication = lazy(() => import("@/pages/authentication/Authentication"));

const AppRoutes = (props) => {

    const { isAuthenticated } = useSelector((state) => state.auth);
    // Public visitors (login/forgot-password/reset) have no session — don't fetch the
    // (authenticated) sidebar menu for them, same as other modules with an inline login route.
    // ERP (Cogent) SSO login writes the auth cookie after the store is created, so the redux
    // flag stays false until a refresh — also check the cookie (as skillPassport does).
    const authProps = getAuthProps();
    const isLoggedIn = isAuthenticated || !!authProps;
    // Remember who the live session belongs to, so an expired one is sent to the right login
    if (authProps) {
        if (authProps.companyId) setCompanyLogin();
        else clearCompanyLogin();
    }
    // Company users with an expired session go to this app's login, everyone else to the auth portal
    const loginPath = isCompanyLogin() ? "/login" : undefined;
    const { sidebarMenuConfig, isFetching } = useSidebarMenuConfig({
        moduleId: MODULE_IDS.usk_placement,
        skip: !isLoggedIn,
    });
    const defaultPath =
        getFirstNavigationPath(sidebarMenuConfig)
        || getFirstNavigationPath(defaultSidebarMenuConfig)
        || "/profile";

    return (
        <Router>
            <Routes>
                <Route exact name="Login" path="/login" element={<Authentication />} />
                <Route exact name="ForgotPassword" path="/forgot-password" element={<Authentication />} />
                <Route exact name="ResetPassword" path="/reset" element={<Authentication />} />

                <Route path="/" element={
                    isFetching ? <PageLoader /> : <Navigate to={defaultPath} replace />
                } />
                <Route name="Layout" path="/"
                    element={
                        <ProtectedRoute sidebarMenuConfig={sidebarMenuConfig} loginPath={loginPath}>
                            {isFetching ? (
                                <PageLoader />
                            ) : (
                                <MainLayout componentRoutes={props.componentRoutes}
                                    sidebarMenuConfig={sidebarMenuConfig || defaultSidebarMenuConfig} />
                            )}
                        </ProtectedRoute>
                    }
                >
                    {props.componentRoutes.map((route) => (
                        <Route key={route.path} path={route.path}
                            name=""
                            element={<route.component />} />
                    ))}

                    {props.componentRoutes.map((route) => (
                        route?.children?.map((childRoute) => (
                            <Route
                                key={childRoute.path}
                                path={childRoute.path}
                                element={<childRoute.component securityKey={childRoute?.securityKey} />}
                            />
                        ))
                    ))}
                </Route>
                <Route path="*" element={<PageNotFound />} />
            </Routes>
        </Router>
    );
};

AppRoutes.propTypes = {
    componentRoutes: PropTypes.arrayOf(
        PropTypes.shape({
            path: PropTypes.string.isRequired,
            component: PropTypes.elementType.isRequired,
            securityKey: PropTypes.string,
            children: PropTypes.arrayOf(
                PropTypes.shape({
                    path: PropTypes.string.isRequired,
                    component: PropTypes.elementType.isRequired,
                    securityKey: PropTypes.string,
                })
            ),
        })
    ).isRequired,
};

export default AppRoutes;
